import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";

/**
 * Animated step-callouts overlaid on a photo.
 *
 * On mount (or when the image enters the viewport) each step fades in with a
 * short stagger, its ring pulses on the target spot, an SVG line arcs out
 * from the ring to the labelled badge, and the badge slides into place.
 *
 * `spots` shape:
 *   {
 *     x: 0-100    // % from left  → target ring position
 *     y: 0-100    // % from top   → target ring position
 *     lx: 0-100   // % from left  → label position
 *     ly: 0-100   // % from top   → label position
 *     title: "Step 1 · Read the tape"
 *     body:  "Bias, structure, level — always in that order."
 *   }
 *
 * Positioning is entirely %-based on the image aspect box, so the callouts
 * stay accurate across breakpoints. On mobile (<640px) badges collapse into
 * a stacked list under the image for readability.
 */
export default function AnnotatedImage({ src, alt, spots = [], caption, masks = [] }) {
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // Trigger the animation only once the image is actually in the viewport —
  // keeps the reveal feeling intentional rather than "already done before
  // the user scrolled here."
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={rootRef} data-testid="annotated-image" className="w-full">
      <div className="relative rounded-3xl overflow-hidden bg-slate-950 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.4)] border border-slate-200/60">
        <img src={src} alt={alt} className="w-full h-auto block" />

        {/* Simple rectangle masks — used to visually hide small artefacts
            in the source image (e.g., leaked indicator labels) without
            having to re-render the underlying screenshot. Each mask:
            { x, y, w, h }  → all %-based, and { color } defaults to the
            chart's light-grey background. */}
        {masks.map((m, i) => (
          <div
            key={`mask-${i}`}
            aria-hidden="true"
            className="absolute pointer-events-none"
            style={{
              left: `${m.x}%`,
              top: `${m.y}%`,
              width: `${m.w}%`,
              height: `${m.h}%`,
              background: m.color || "#f4f4f5",
            }}
          />
        ))}

        {/* SVG overlay for the arcing lines from ring → label */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <marker id="arrow-head" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#F59E0B" />
            </marker>
          </defs>
          {spots.map((s, i) => {
            // Simple quadratic curve for organic feel — control point offset
            // perpendicular to the line direction.
            const dx = s.lx - s.x;
            const dy = s.ly - s.y;
            const cx = s.x + dx / 2 - dy * 0.15;
            const cy = s.y + dy / 2 + dx * 0.15;
            const path = `M ${s.lx} ${s.ly} Q ${cx} ${cy} ${s.x} ${s.y}`;
            return (
              <path
                key={i}
                d={path}
                fill="none"
                stroke="#F59E0B"
                strokeWidth="0.35"
                strokeLinecap="round"
                strokeDasharray="1.5 1.5"
                markerEnd="url(#arrow-head)"
                vectorEffect="non-scaling-stroke"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(4px)",
                  transition: `opacity 700ms ease ${400 + i * 250}ms, transform 700ms ease ${400 + i * 250}ms`,
                }}
              />
            );
          })}
        </svg>

        {/* Target rings (over the image spot) */}
        {spots.map((s, i) => (
          <div
            key={`ring-${i}`}
            className="absolute pointer-events-none"
            style={{
              left: `${s.x}%`,
              top: `${s.y}%`,
              transform: "translate(-50%, -50%)",
              opacity: visible ? 1 : 0,
              transition: `opacity 500ms ease ${200 + i * 250}ms`,
            }}
          >
            <div className="relative flex items-center justify-center">
              <div className="absolute w-14 h-14 rounded-full bg-amber-500/25 animate-ping" />
              <div className="relative w-10 h-10 rounded-full bg-amber-500 border-2 border-white shadow-[0_4px_14px_rgba(245,158,11,0.55)] flex items-center justify-center text-slate-950 font-display font-black text-base">
                {i + 1}
              </div>
            </div>
          </div>
        ))}

        {/* Labels — desktop only, positioned around the image */}
        {/* Each label is anchored by its TOP-LEFT corner at (lx, ly) — the
            caller chooses coordinates so the label naturally fits inside
            the image bounds. Keeps the placement math predictable. */}
        <div className="hidden md:block">
          {spots.map((s, i) => (
            <div
              key={`label-${i}`}
              data-testid={`annotated-step-${i + 1}`}
              className="absolute"
              style={{
                left: `${s.lx}%`,
                top: `${s.ly}%`,
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(6px)",
                transition: `opacity 600ms ease ${600 + i * 250}ms, transform 600ms ease ${600 + i * 250}ms`,
              }}
            >
              <div className="max-w-[240px] rounded-xl bg-white/95 backdrop-blur border border-slate-200 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.35)] p-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">{t("annotated.step", { n: i + 1 })}</div>
                <div className="mt-0.5 text-sm font-display font-bold text-slate-900 leading-snug">{s.title}</div>
                <div className="mt-1 text-[12px] text-slate-600 leading-snug">{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {caption && (
        <p className="mt-4 text-center text-xs text-slate-500 italic">{caption}</p>
      )}

      {/* Mobile stack — the labels live below the image for legibility */}
      <div className="md:hidden mt-6 space-y-3">
        {spots.map((s, i) => (
          <div key={`m-${i}`} className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-500 text-slate-950 font-display font-black flex items-center justify-center shrink-0 shadow">
              {i + 1}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">{t("annotated.step", { n: i + 1 })}</div>
              <div className="text-sm font-display font-bold text-slate-900 mt-0.5">{s.title}</div>
              <div className="text-[13px] text-slate-600 mt-0.5 leading-relaxed">{s.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
